import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Box,
  CircularProgress
} from '@mui/material';
import api, { handleApiError } from '../../services/api';

// Opened from the link in the password reset email:
// /reset-password/:uidb64/:token
const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const { uidb64, token } = useParams();
  const [formData, setFormData] = useState({
    password: '',
    password2: ''
  });
  // 'checking' -> 'valid' | 'invalid' -> 'done'
  const [linkState, setLinkState] = useState('checking');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`auth/password-reset/${uidb64}/${token}/`)
      .then(() => setLinkState('valid'))
      .catch((error) => {
        setError(handleApiError(error));
        setLinkState('invalid');
      });
  }, [uidb64, token]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.password2) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.patch('auth/password-reset-complete/', {
        password: formData.password,
        uidb64,
        token
      });
      setLinkState('done');
    } catch (error) {
      setError(handleApiError(error));
    } finally {
      setLoading(false);
    }
  };

  const renderBody = () => {
    if (linkState === 'checking') {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (linkState === 'invalid') {
      return (
        <>
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            onClick={() => navigate('/forgot-password')}
          >
            Request a New Link
          </Button>
        </>
      );
    }

    if (linkState === 'done') {
      return (
        <>
          <Alert severity="success" sx={{ mt: 2 }}>
            Your password has been reset. You can now log in with your new password.
          </Alert>
          <Button
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            onClick={() => navigate('/login')}
          >
            Go to Login
          </Button>
        </>
      );
    }

    return (
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
        <TextField
          margin="normal"
          required
          fullWidth
          name="password"
          label="New Password"
          type="password"
          id="password"
          autoComplete="new-password"
          autoFocus
          value={formData.password}
          onChange={handleChange}
        />

        <TextField
          margin="normal"
          required
          fullWidth
          name="password2"
          label="Confirm New Password"
          type="password"
          id="password2"
          autoComplete="new-password"
          value={formData.password2}
          onChange={handleChange}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
          disabled={loading}
        >
          {loading ? 'Resetting...' : 'Reset Password'}
        </Button>
      </Box>
    );
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Reset Password
          </Typography>
          {renderBody()}
        </Paper>
      </Box>
    </Container>
  );
};

export default ResetPasswordPage;
